import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { CheckCircle2, Clock3, ListTodo, TimerReset, UserRound } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmationModal';
import { deleteTask, getTasks, updateTaskDetail } from '../../hooks/tasks';
import { Task } from '../../utils/types';
import {
  formatTaskDate,
  getTaskSearchIndex,
  getTaskUrgencyColor,
  getTaskUrgencyLabel,
  isTaskComplete,
  isTaskDueToday,
  isTaskOverdue,
  TaskCommandBar,
  TaskCreateButton,
  TaskDashboardSkeleton,
  TaskEmptyState,
  TaskHero,
  TaskMetricStrip,
  TaskPriorityBadge,
  TaskShell,
  TaskStatusBadge,
} from './components/taskUi';

const normalizeTasks = (data: unknown): Task[] => {
  if (Array.isArray(data)) {
    return data as Task[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Task[] }).results;
  }

  return [];
};

const sortTasks = (tasks: Task[], sort: string) => {
  const next = [...tasks];

  switch (sort) {
    case 'title-asc':
      return next.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    case 'priority-desc': {
      const priorityOrder: Record<string, number> = {
        urgent: 4,
        high: 3,
        medium: 2,
        low: 1,
      };
      return next.sort(
        (a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0),
      );
    }
    case 'created-desc':
      return next.sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime(),
      );
    case 'due-desc':
      return next.sort(
        (a, b) => new Date(b.due_date || '').getTime() - new Date(a.due_date || '').getTime(),
      );
    case 'due-asc':
    default:
      return next.sort(
        (a, b) => new Date(a.due_date || '').getTime() - new Date(b.due_date || '').getTime(),
      );
  }
};

type TaskItemProps = {
  task: Task;
  onOpen: (taskId: string) => void;
  onEdit: (taskId: string) => void;
  onComplete: (task: Task) => void;
  onDelete: (task: Task) => void;
};

const TaskTableRow = ({ task, onOpen, onEdit, onComplete, onDelete }: TaskItemProps) => (
  <Tr _hover={{ bg: 'gray.50' }} transition="background 0.16s ease">
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text fontWeight="800" color="gray.900" cursor="pointer" onClick={() => onOpen(task.id)}>
          {task.title}
        </Text>
        <Text fontSize="sm" color="gray.500" noOfLines={1} maxW="420px">
          {task.description || task.remarks || 'No extra details'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.900" fontWeight="600">
          {task.assigned_to_name || 'Unassigned'}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {task.created_by_name || 'No creator shown'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <TaskPriorityBadge priority={task.priority} />
    </Td>
    <Td py={4}>
      <TaskStatusBadge status={task.status} />
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color={getTaskUrgencyColor(task)} fontWeight="700">
          {getTaskUrgencyLabel(task)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          Starts {formatTaskDate(task.start_date)}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <HStack justify="flex-end" spacing={1}>
        <Tooltip label="Open task" hasArrow>
          <IconButton aria-label="Open task" icon={<ViewIcon />} variant="ghost" onClick={() => onOpen(task.id)} />
        </Tooltip>
        <Tooltip label="Edit task" hasArrow>
          <IconButton aria-label="Edit task" icon={<EditIcon />} variant="ghost" onClick={() => onEdit(task.id)} />
        </Tooltip>
        {!isTaskComplete(task) ? (
          <Tooltip label="Mark complete" hasArrow>
            <IconButton
              aria-label="Mark complete"
              icon={<Icon as={CheckCircle2 as ElementType} />}
              variant="ghost"
              colorScheme="green"
              onClick={() => onComplete(task)}
            />
          </Tooltip>
        ) : null}
        <Tooltip label="Delete task" hasArrow>
          <IconButton
            aria-label="Delete task"
            icon={<DeleteIcon />}
            variant="ghost"
            colorScheme="red"
            onClick={() => onDelete(task)}
          />
        </Tooltip>
      </HStack>
    </Td>
  </Tr>
);

const TaskCard = ({ task, onOpen, onEdit, onComplete, onDelete }: TaskItemProps) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="22px"
    px={4}
    py={4}
    boxShadow="0 12px 30px rgba(15, 23, 42, 0.04)"
  >
    <Stack spacing={4}>
      <HStack justify="space-between" align="flex-start">
        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="800" color="gray.900">
            {task.title}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {task.assigned_to_name || 'Unassigned'}
          </Text>
        </VStack>
        <TaskStatusBadge status={task.status} />
      </HStack>

      <Text fontSize="sm" color="gray.600" noOfLines={2}>
        {task.description || task.remarks || 'No extra details'}
      </Text>

      <SimpleGrid columns={2} spacing={3}>
        <Box>
          <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.1em" fontWeight="800" color="gray.500" mb={1}>
            Priority
          </Text>
          <TaskPriorityBadge priority={task.priority} />
        </Box>
        <Box>
          <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.1em" fontWeight="800" color="gray.500" mb={1}>
            Due
          </Text>
          <Text fontSize="sm" fontWeight="700" color={getTaskUrgencyColor(task)}>
            {getTaskUrgencyLabel(task)}
          </Text>
        </Box>
      </SimpleGrid>

      <HStack justify="space-between">
        <Button variant="ghost" onClick={() => onOpen(task.id)}>
          Open
        </Button>
        <HStack spacing={1}>
          <IconButton aria-label="Edit task" icon={<EditIcon />} variant="ghost" onClick={() => onEdit(task.id)} />
          {!isTaskComplete(task) ? (
            <IconButton
              aria-label="Mark complete"
              icon={<Icon as={CheckCircle2 as ElementType} />}
              variant="ghost"
              colorScheme="green"
              onClick={() => onComplete(task)}
            />
          ) : null}
          <IconButton aria-label="Delete task" icon={<DeleteIcon />} variant="ghost" colorScheme="red" onClick={() => onDelete(task)} />
        </HStack>
      </HStack>
    </Stack>
  </Box>
);

const TasksDashboard = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [sort, setSort] = useState('due-asc');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getTasksData = async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    const response = await getTasks(orgId);
    if (response.success === true) {
      setTasks(normalizeTasks(response?.data));
    } else {
      toast({
        title: 'Could not load tasks',
        description: 'Please refresh and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    getTasksData();
  }, [orgId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filteredTasks = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = tasks.filter((task) => {
      const matchesSearch = !query || getTaskSearchIndex(task).includes(query);
      const matchesStatus = status === 'all' || task.status === status;
      const matchesPriority = priority === 'all' || task.priority === priority;
      return matchesSearch && matchesStatus && matchesPriority;
    });

    return sortTasks(filtered, sort);
  }, [tasks, search, status, priority, sort]);

  const openTasks = tasks.filter((task) => !isTaskComplete(task));
  const dueToday = tasks.filter((task) => isTaskDueToday(task)).length;
  const overdue = tasks.filter((task) => isTaskOverdue(task)).length;
  const completed = tasks.filter((task) => task.status === 'completed').length;

  const metrics = [
    {
      label: 'Open tasks',
      value: String(openTasks.length),
      helper: 'Current active work',
      icon: ListTodo as ElementType,
      color: '#166534',
    },
    {
      label: 'Due today',
      value: String(dueToday),
      helper: 'Needs action today',
      icon: Clock3 as ElementType,
      color: '#D97706',
    },
    {
      label: 'Overdue',
      value: String(overdue),
      helper: 'Past the due date',
      icon: TimerReset as ElementType,
      color: '#DC2626',
    },
    {
      label: 'Completed',
      value: String(completed),
      helper: 'Finished work',
      icon: CheckCircle2 as ElementType,
      color: '#15803D',
    },
  ];

  const handleComplete = async (task: Task) => {
    if (!orgId) {
      return;
    }

    setProcessingId(task.id);
    const response = await updateTaskDetail(orgId, task.id, 'status', 'completed');
    setProcessingId(null);

    if (response?.success === true) {
      toast({
        title: 'Task completed',
        description: `${task.title} was marked complete.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await getTasksData();
    } else {
      toast({
        title: 'Could not update task',
        description: response?.data?.detail || 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!orgId || !selectedTask) {
      return;
    }

    setProcessingId(selectedTask.id);
    const response = await deleteTask(orgId, selectedTask.id);
    setProcessingId(null);

    if (response?.success === true) {
      toast({
        title: 'Task deleted',
        description: `${selectedTask.title} was removed.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      setSelectedTask(null);
      await getTasksData();
    } else {
      toast({
        title: 'Could not delete task',
        description: response?.data?.detail || 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <TaskShell>
        <TaskDashboardSkeleton />
      </TaskShell>
    );
  }

  return (
    <TaskShell>
      <TaskHero
        eyebrow="Tasks"
        title="Work queue and execution overview"
        description="Track what needs attention, where deadlines are slipping, and who owns the next move."
        primaryAction={<TaskCreateButton onClick={() => navigate(`/organization/${orgId}/tasks/new`)} />}
      />

      <TaskMetricStrip items={metrics} />

      <TaskCommandBar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        priority={priority}
        onPriorityChange={setPriority}
        sort={sort}
        onSortChange={setSort}
        inputRef={searchRef}
      />

      {filteredTasks.length === 0 ? (
        <Box mt={6} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="24px">
          <TaskEmptyState
            title={tasks.length === 0 ? 'No tasks yet' : 'No tasks match these filters'}
            description={
              tasks.length === 0
                ? 'Create the first task to start assigning work, scheduling deadlines, and tracking delivery.'
                : 'Clear or adjust the current filters to bring tasks back into view.'
            }
            action={
              tasks.length === 0 ? (
                <TaskCreateButton onClick={() => navigate(`/organization/${orgId}/tasks/new`)} />
              ) : (
                <Button variant="outline" borderRadius="16px" onClick={() => {
                  setSearch('');
                  setStatus('all');
                  setPriority('all');
                  setSort('due-asc');
                }}>
                  Clear filters
                </Button>
              )
            }
          />
        </Box>
      ) : (
        <Box mt={6}>
          <Box display={{ base: 'none', lg: 'block' }} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="24px" overflow="hidden" boxShadow="0 18px 40px rgba(15, 23, 42, 0.05)">
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Task</Th>
                    <Th>Ownership</Th>
                    <Th>Priority</Th>
                    <Th>Status</Th>
                    <Th>Schedule</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredTasks.map((task) => (
                    <TaskTableRow
                      key={task.id}
                      task={task}
                      onOpen={(taskId) => navigate(`/organization/${orgId}/tasks/${taskId}/view`)}
                      onEdit={(taskId) => navigate(`/organization/${orgId}/tasks/${taskId}/edit`)}
                      onComplete={handleComplete}
                      onDelete={(nextTask) => {
                        setSelectedTask(nextTask);
                        onOpen();
                      }}
                    />
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} display={{ base: 'grid', lg: 'none' }}>
            {filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onOpen={(taskId) => navigate(`/organization/${orgId}/tasks/${taskId}/view`)}
                onEdit={(taskId) => navigate(`/organization/${orgId}/tasks/${taskId}/edit`)}
                onComplete={handleComplete}
                onDelete={(nextTask) => {
                  setSelectedTask(nextTask);
                  onOpen();
                }}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      <ConfirmModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSelectedTask(null);
        }}
        handleConfirm={handleDeleteConfirm}
        isLoading={processingId === selectedTask?.id}
        AreYouSureQuestion="Delete task?"
        AreYouSureTextExplanation={`This will remove ${selectedTask?.title || 'this task'} from the workspace.`}
        confirmButtonText="Delete task"
        confirmButtonColor="#DC2626"
        colorScheme="red"
      />
    </TaskShell>
  );
};

export default TasksDashboard;
